/**
 * Session History Store
 *
 * Provides full undo/redo within a session by maintaining a stack of complete
 * artifact snapshots. Session start is history[0] and acts as undo boundary.
 * Auto-capture via store subscriptions with debouncing.
 */

import { create } from 'zustand';
import type { WorkflowDefinition } from '@/types/workflowDefinition';
import type { ConnectionsConfiguration } from '@/types/logicApps';
import { useWorkflowStore } from './workflowStore';
import { useDraftModeStore } from './draftModeStore';

export interface ArtifactSnapshot {
  id: string;
  timestamp: number;
  description: string;
  workflow: WorkflowDefinition;
  connections: ConnectionsConfiguration | null;
  parameters: Record<string, unknown> | null;
  notes: Record<string, unknown> | null;
}

interface SessionHistoryState {
  history: ArtifactSnapshot[];
  currentIndex: number;
  maxSnapshots: number;
}

interface SessionHistoryActions {
  clearSession: () => void;
  initSession: (
    artifacts: Omit<ArtifactSnapshot, 'id' | 'timestamp' | 'description'>,
    description: string
  ) => void;
  captureSnapshot: (
    artifacts: Omit<ArtifactSnapshot, 'id' | 'timestamp' | 'description'>,
    description: string
  ) => void;
  undo: () => boolean;
  redo: () => boolean;
  canUndo: () => boolean;
  canRedo: () => boolean;
  revertToSessionStart: () => void;
  hasSessionChanges: () => boolean;
}

type SessionHistoryStore = SessionHistoryState & SessionHistoryActions;

const initialState: SessionHistoryState = {
  history: [],
  currentIndex: -1,
  maxSnapshots: 50,
};

export const useSessionHistoryStore = create<SessionHistoryStore>()((set, get) => ({
  ...initialState,

  clearSession: () => {
    set(initialState);
  },

  initSession: (artifacts, description) => {
    const snapshot: ArtifactSnapshot = {
      id: crypto.randomUUID(),
      timestamp: Date.now(),
      description,
      workflow: structuredClone(artifacts.workflow),
      connections: artifacts.connections ? structuredClone(artifacts.connections) : null,
      parameters: artifacts.parameters ? structuredClone(artifacts.parameters) : null,
      notes: artifacts.notes ? structuredClone(artifacts.notes) : null,
    };

    set({
      history: [snapshot],
      currentIndex: 0,
    });
  },

  captureSnapshot: (artifacts, description) => {
    set((state) => {
      const snapshot: ArtifactSnapshot = {
        id: crypto.randomUUID(),
        timestamp: Date.now(),
        description,
        workflow: structuredClone(artifacts.workflow),
        connections: artifacts.connections ? structuredClone(artifacts.connections) : null,
        parameters: artifacts.parameters ? structuredClone(artifacts.parameters) : null,
        notes: artifacts.notes ? structuredClone(artifacts.notes) : null,
      };

      // Truncate any future history if we're not at the end
      const truncatedHistory = state.history.slice(0, state.currentIndex + 1);

      // Add new snapshot
      let newHistory = [...truncatedHistory, snapshot];

      // Enforce max snapshots, but always keep session start (index 0)
      if (newHistory.length > state.maxSnapshots && newHistory[0]) {
        // Remove oldest non-session-start snapshot
        newHistory = [newHistory[0], ...newHistory.slice(2)];
      }

      return {
        history: newHistory,
        currentIndex: newHistory.length - 1,
      };
    });
  },

  undo: () => {
    const { history, currentIndex } = get();

    // Can't undo past session start (index 0)
    if (currentIndex <= 0) {
      return false;
    }

    const newIndex = currentIndex - 1;
    const snapshot = history[newIndex];

    // Guard against corrupted state - snapshot must exist
    if (!snapshot) {
      console.error('[SessionHistory] undo: snapshot not found at index', newIndex);
      return false;
    }

    try {
      // Apply the snapshot to workflow/connection stores
      const { setWorkflowFiles } = useWorkflowStore.getState();
      setWorkflowFiles(snapshot.workflow, snapshot.connections);

      // Restore parameters and notes to draft mode store
      if (snapshot.parameters || snapshot.notes) {
        const { setDraftArtifacts } = useDraftModeStore.getState();
        setDraftArtifacts({
          parameters: snapshot.parameters ?? undefined,
          notes: snapshot.notes ?? undefined,
        });
      }

      set({ currentIndex: newIndex });
      return true;
    } catch (error) {
      console.error('[SessionHistory] undo: failed to apply snapshot', error);
      return false;
    }
  },

  redo: () => {
    const { history, currentIndex } = get();

    // Can't redo past latest
    if (currentIndex >= history.length - 1) {
      return false;
    }

    const newIndex = currentIndex + 1;
    const snapshot = history[newIndex];

    // Guard against corrupted state - snapshot must exist
    if (!snapshot) {
      console.error('[SessionHistory] redo: snapshot not found at index', newIndex);
      return false;
    }

    try {
      // Apply the snapshot to workflow/connection stores
      const { setWorkflowFiles } = useWorkflowStore.getState();
      setWorkflowFiles(snapshot.workflow, snapshot.connections);

      // Restore parameters and notes to draft mode store
      if (snapshot.parameters || snapshot.notes) {
        const { setDraftArtifacts } = useDraftModeStore.getState();
        setDraftArtifacts({
          parameters: snapshot.parameters ?? undefined,
          notes: snapshot.notes ?? undefined,
        });
      }

      set({ currentIndex: newIndex });
      return true;
    } catch (error) {
      console.error('[SessionHistory] redo: failed to apply snapshot', error);
      return false;
    }
  },

  canUndo: () => {
    const { currentIndex } = get();
    return currentIndex > 0;
  },

  canRedo: () => {
    const { history, currentIndex } = get();
    return currentIndex < history.length - 1;
  },

  revertToSessionStart: () => {
    const { history } = get();
    const sessionStart = history[0];

    if (!sessionStart) {
      return;
    }

    // Apply session start snapshot
    const { setWorkflowFiles } = useWorkflowStore.getState();
    setWorkflowFiles(sessionStart.workflow, sessionStart.connections);

    // Restore parameters and notes to draft mode store
    if (sessionStart.parameters || sessionStart.notes) {
      const { setDraftArtifacts } = useDraftModeStore.getState();
      setDraftArtifacts({
        parameters: sessionStart.parameters ?? undefined,
        notes: sessionStart.notes ?? undefined,
      });
    }

    set({ currentIndex: 0 });
  },

  hasSessionChanges: () => {
    const { history } = get();
    // Has changes if there's more than just the session start snapshot
    return history.length > 1;
  },
}));
